import { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  Activity,
  ExternalLink,
  HeartPulse,
  IdCard,
  MapPin,
  Phone,
  ShieldAlert,
  UserRound,
  X,
} from 'lucide-react';
import { joinFullName } from '../../utils/patientName';
import { patientCategoryLabel } from '../../utils/patientCategory';
import { formatDateLabel } from '../../utils/dateFormat';
import {
  composePatientAddress,
  permanentAddressParts,
  presentAddressParts,
} from '../../utils/patientAddress';

/** Care-affecting conditions carried on the patient record. */
const medicalFlagDefs: { key: string; label: string }[] = [
  { key: 'drugAllergy', label: 'Drug Allergy' },
  { key: 'foodAllergy', label: 'Food Allergy' },
  { key: 'pregnancy', label: 'Pregnancy' },
  { key: 'highRisk', label: 'High Risk' },
  { key: 'infectiousDisease', label: 'Infectious Disease' },
  { key: 'fallRisk', label: 'Fall Risk' },
  { key: 'wheelchair', label: 'Wheelchair' },
  { key: 'hearingImpairment', label: 'Hearing Impairment' },
  { key: 'visualImpairment', label: 'Visual Impairment' },
];

interface PatientDetailsModalProps {
  open: boolean;
  /** Patient record attached to the admission (`admission.patient`). */
  patient: any;
  /** Close the modal. */
  onClose: () => void;
  /** Optional deep link to the full patient profile. */
  profileHref?: string;
}

/** Turns stored enum-like values (`single`, `semi_private`) into labels. */
function titleCase(value?: string): string {
  if (!value) return '';
  return value
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/** Compact label/value row used inside the modal panels. */
function Row({ label, value }: { label: string; value?: string }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1">
      <span className="shrink-0 text-[11px] text-text-muted">{label}</span>
      <span className="min-w-0 break-words text-right text-[13px] font-medium leading-snug text-text">
        {value?.trim() ? value : '—'}
      </span>
    </div>
  );
}

/** Small headed panel that groups related patient fields. */
function Panel({
  icon: Icon,
  title,
  children,
  className = '',
}: {
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`rounded-xl border border-border/70 bg-background/50 p-3 ${className}`}
    >
      <div className="mb-1.5 flex items-center gap-1.5">
        <Icon size={13} className="text-primary" />
        <h3 className="text-[10px] font-bold uppercase tracking-wider text-text-muted">
          {title}
        </h3>
      </div>
      <div className="divide-y divide-border/60">{children}</div>
    </section>
  );
}

/**
 * Read-only patient profile shown from the admission workspace. It carries the
 * demographics, contacts, addresses and medical flags that used to live in a
 * long "Patient Information" panel at the bottom of the page, so the admission
 * screen stays compact without losing the information.
 */
export function PatientDetailsModal({
  open,
  patient,
  onClose,
  profileHref,
}: PatientDetailsModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const fullName = useMemo(
    () => joinFullName(patient) || patient?.registrationNumber || 'Unknown patient',
    [patient],
  );

  const medicalFlags = useMemo(
    () => medicalFlagDefs.filter((flag) => Boolean(patient?.[flag.key])),
    [patient],
  );

  if (!open || !patient) return null;

  const initials =
    `${patient.firstName?.[0] || ''}${patient.lastName?.[0] || ''}`.trim() ||
    (patient.registrationNumber || 'P').slice(0, 2).toUpperCase();

  const ageLabel = patient.age ? `${patient.age} yrs` : '—';
  const dobLabel = patient.dateOfBirth
    ? `${formatDateLabel(patient.dateOfBirth)}${patient.estimatedAge ? ' (est.)' : ''}`
    : '—';
  const permanentAddress = composePatientAddress(permanentAddressParts(patient)) || '—';
  const presentAddress = composePatientAddress(presentAddressParts(patient)) || '—';

  const chips = [
    patient.patientCategory ? patientCategoryLabel(patient.patientCategory) : '',
    titleCase(patient.gender),
    patient.age ? `${patient.age} yrs` : '',
    patient.bloodGroup ? `Blood ${patient.bloodGroup}` : '',
  ].filter(Boolean);

  const modal = (
    <div
      className="modal-overlay-enter fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/60 p-3 backdrop-blur-md sm:p-5"
      role="dialog"
      aria-modal="true"
      aria-labelledby="patient-details-title"
      onClick={onClose}
    >
      <div
        className="modal-panel-enter flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-border/70 bg-surface shadow-[0_24px_80px_-20px_rgba(15,23,42,0.45)]"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="relative shrink-0 overflow-hidden border-b border-border/70 bg-gradient-to-br from-primary/15 via-surface to-secondary/10 px-4 py-3.5 sm:px-5">
          <div className="pointer-events-none absolute -right-8 -top-12 h-32 w-32 rounded-full bg-primary/10 blur-2xl" />
          <div className="relative flex items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary text-sm font-bold uppercase tracking-wide text-white shadow-lg shadow-primary/25">
              {initials}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-primary">
                Patient
              </p>
              <h2
                id="patient-details-title"
                className="truncate text-base font-bold leading-tight text-text sm:text-lg"
              >
                {fullName}
              </h2>
              <p className="truncate text-[11px] font-mono text-text-muted">
                {patient.registrationNumber || '—'}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 rounded-xl border border-border/70 bg-surface/80 p-1.5 text-text-muted transition hover:bg-background hover:text-text"
              aria-label="Close patient details"
            >
              <X size={16} />
            </button>
          </div>

          {chips.length > 0 && (
            <div className="relative mt-2.5 flex flex-wrap gap-1.5">
              {chips.map((chip) => (
                <span
                  key={chip}
                  className="rounded-lg border border-border/70 bg-surface/70 px-2 py-0.5 text-[10px] font-semibold text-text-muted"
                >
                  {chip}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto p-3.5 sm:p-4">
          {medicalFlags.length > 0 && (
            <div className="rounded-xl border border-danger/20 bg-danger/5 p-2.5">
              <div className="mb-1.5 flex items-center gap-1.5">
                <ShieldAlert size={13} className="text-danger" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-danger">
                  Medical flags
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {medicalFlags.map((flag) => (
                  <span
                    key={flag.key}
                    className="inline-flex items-center gap-1 rounded-lg border border-danger/20 bg-danger/10 px-2 py-0.5 text-[11px] font-medium text-danger"
                  >
                    <ShieldAlert size={11} />
                    {flag.label}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="grid gap-2.5 sm:grid-cols-2">
            <Panel icon={UserRound} title="Personal">
              <Row label="Gender" value={titleCase(patient.gender)} />
              <Row label="Date of birth" value={dobLabel} />
              <Row label="Age" value={ageLabel} />
              <Row label="Blood group" value={patient.bloodGroup} />
              <Row label="Marital status" value={titleCase(patient.maritalStatus)} />
              <Row label="Nationality" value={patient.nationality} />
              <Row label="Occupation" value={patient.occupation} />
            </Panel>

            <Panel icon={Phone} title="Contact">
              <Row label="Mobile" value={patient.mobileNumber} />
              <Row label="Secondary mobile" value={patient.secondaryMobile} />
              <Row label="Email" value={patient.email} />
              <Row
                label="Category"
                value={
                  patient.patientCategory
                    ? patientCategoryLabel(patient.patientCategory)
                    : '—'
                }
              />
              <Row label="National ID" value={patient.nationalId} />
            </Panel>

            <Panel icon={MapPin} title="Addresses" className="sm:col-span-2">
              <Row label="Present" value={presentAddress} />
              <Row label="Permanent" value={permanentAddress} />
            </Panel>

            <Panel icon={HeartPulse} title="Emergency & guardian">
              <Row label="Contact" value={patient.emergencyContactName} />
              <Row
                label="Relation"
                value={titleCase(patient.emergencyContactRelation)}
              />
              <Row label="Phone" value={patient.emergencyContactPhone} />
              <Row label="Alternate phone" value={patient.emergencyContactAltPhone} />
              <Row label="Guardian" value={patient.guardianName} />
              <Row label="Guardian phone" value={patient.guardianPhone} />
            </Panel>

            <Panel icon={IdCard} title="Registration">
              <Row label="Patient ID" value={patient.registrationNumber} />
              <Row label="Registration type" value={titleCase(patient.registrationType)} />
              <Row
                label="Registered on"
                value={
                  patient.registrationDate
                    ? formatDateLabel(patient.registrationDate)
                    : '—'
                }
              />
            </Panel>
          </div>

          {patient.medicalNote && (
            <div className="rounded-xl border border-border/70 bg-background/50 p-3">
              <div className="mb-1 flex items-center gap-1.5">
                <Activity size={13} className="text-primary" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-text-muted">
                  Medical note
                </span>
              </div>
              <p className="whitespace-pre-wrap text-[13px] text-text-muted">
                {patient.medicalNote}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border/70 bg-surface px-4 py-2.5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-border bg-surface px-3.5 py-1.5 text-xs font-semibold text-text-muted transition hover:bg-background hover:text-text"
          >
            Close
          </button>
          {profileHref && (
            <Link
              to={profileHref}
              className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-3.5 py-1.5 text-xs font-semibold text-white transition hover:bg-primary-dark"
            >
              <ExternalLink size={13} />
              Full Profile
            </Link>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
